import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Header from '../../components/header/header.component';
import BasicButton from '../../components/basic-button/basic-button.component';
import Table from '../../components/table/table.component';
import { TableRow } from '../../types/table-row.type';
import { userSdk } from '../../services/user.service';
import ResponseCodes from '../../constants/response.codes';
import { Company } from '../../types/company.type';
import FilteringButton from '../../components/filtering-button/filtering-button.component';
import CreateCompany from '../../components/create-company/create-company';
import { CompanyCreation } from '../../types/company-creation.type';
import Toast, { ToastType } from '../../components/toast/toast';
import Modal from '../../components/modal/modal.component';
import FileInput from '../../components/file-input/file-input';
import CustomSelect, {
    SelectOption,
} from '../../components/custom-select/custom-select.component';
import { useAuthStore, useServiceStore } from '../../store';
import { Plan } from '../../types/plan.type';
import { subscriptionSdk } from '../../services/subscription.service';
import CompanySearchInput from '../../components/company-search-input/company-search-input';
import View from '../../components/view/view';
import { RolePermission } from '../../types/user.type';
import { hasPermissions } from '../../utils/permissions';
import Note from '../../components/note/note';

import styles from './companies.module.scss';

type SubmitBulkUpload = {
    company?: number;
    action: number;
    planId?: number;
    file: File;
};

const CompaniesView = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams({ page: '1', count: '10' });

    const { plans, fetchPlans } = useServiceStore();
    const { permissions } = useAuthStore();

    const [totalPages, setTotalPages] = useState(1);
    const [tableRows, setTableRows] = useState<TableRow[]>([]);
    const [createCompanyShown, setCreateCompanyShown] = useState(false);
    const [toast, setToast] = useState({
        state: ToastType.loading,
        text: 'Cargando...',
        shown: false,
    });
    const [bulkUploadShown, setBulkUploadShown] = useState(false);
    const [searchedCompanies, setSearchedCompanies] = useState<Company[]>([]);

    useEffect(() => {
        fetchCompanies(mapParams(params), permissions);
    }, [params, permissions]);

    useEffect(() => {
        bulkUploadShown && fetchPlans({ active: true });
    }, [bulkUploadShown]);

    const mapParams = useCallback((params: URLSearchParams) => {
        const filters: Record<string, string> = {};
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }

        return filters;
    }, []);

    const goToPage = useCallback(
        (page: number) => {
            if (page < 1 || page > totalPages) return;

            params.set('page', page.toString());
            setParams(params);
        },
        [params, totalPages]
    );

    const fetchCompanies = useCallback(
        async (filters: any, permissions: RolePermission[]) => {
            if (!hasPermissions('read-companies', permissions)) return;

            const response = await userSdk.getCompanies(filters);
            if (response.code !== ResponseCodes.PROCESS_OK) {
                return console.error('Error getting companies:', response);
            }
            const { result, total } = response.data;
            setTotalPages(Math.ceil(total / Number(params.get('count'))));
            mapTableRows(result);
        },
        [params]
    );

    const mapTableRows = (data: any) => {
        setTableRows(
            data.map((company: Company) => ({
                id: company.id,
                data: [
                    { type: 'number', value: company.id },
                    {
                        type: 'string',
                        value: company.displayName ?? company.name,
                    },
                    { type: 'string', value: company.contact?.name ?? '--' },
                    { type: 'string', value: company.contact?.address ?? '--' },
                    {
                        type: 'date',
                        value: new Date(company.createdAt),
                    },
                ],
            }))
        );
    };

    const addFilter = useCallback(
        (attr: string, value: string) => {
            if (!value || value.toString().trim().length === 0) {
                params.delete(attr);
            } else {
                params.set(attr, value);
            }
            params.set('page', '1');
            setParams(params);
        },
        [params]
    );

    const goToCompanyDetails = useCallback(
        (id: number) => navigate(`/companies/${id}`),
        [navigate]
    );

    const createCompany = useCallback(
        async (data: CompanyCreation) => {
            setCreateCompanyShown(false);
            setToast({
                text: 'Cargando...',
                state: ToastType.loading,
                shown: true,
            });
            const response = await userSdk.createCompany(data);
            if (response.code !== ResponseCodes.PROCESS_OK) {
                console.error('Error:', response);
                setToast({
                    text: `Error cargando la empresa: ${response.message}`,
                    state: ToastType.error,
                    shown: true,
                });
            } else {
                setToast({
                    text: 'Empresa creada existosamente',
                    state: ToastType.success,
                    shown: true,
                });
                fetchCompanies(mapParams(params), permissions);
            }
            setTimeout(() => {
                setToast((toast) => ({ ...toast, shown: false }));
            }, 4000);
        },
        [permissions]
    );

    const submitBulkUpload = async (data: SubmitBulkUpload) => {
        setBulkUploadShown(false);
        setToast({
            state: ToastType.loading,
            text: 'Cargando archivo...',
            shown: true,
        });
        const response = await subscriptionSdk.bulkUpload(data);
        if (response.code !== ResponseCodes.PROCESS_OK) {
            console.error('Error en carga masiva:', response);
            setToast({
                state: ToastType.error,
                text: `Error cargando archivo: ${response.message}`,
                shown: true,
            });
        } else {
            setToast({
                state: ToastType.success,
                text: `Archivo cargado exitosamente`,
                shown: true,
            });
        }
        setTimeout(() => {
            setToast((toast) => ({ ...toast, shown: false }));
        }, 4000);
    };

    const searchCompanyByName = async (query: string) => {
        const response = await userSdk.searchCompanyByName(query);
        if (response.code === ResponseCodes.PROCESS_OK) {
            setSearchedCompanies(response.data);
        }
    };

    return (
        <View permissions={permissions} requiredPermission="read-companies">
            <Header
                title={'Compañías'}
                subtitle={'Lista de las compañias registradas en PANA'}
            />
            <article className={styles.mainButtonsContainer}>
                <BasicButton
                    text={'Crear compañía'}
                    onClick={() => setCreateCompanyShown(true)}
                    disabled={!hasPermissions('create-companies', permissions)}
                    disabledReason="No tienes permisos"
                />
                <BasicButton
                    text={'Incluir usuario corporativo'}
                    onClick={() => navigate('/companies/add-user')}
                    disabled={!hasPermissions('update-companies', permissions)}
                    disabledReason="No tienes permisos"
                />
                <BasicButton
                    text={'Activación/Desactivación masiva'}
                    onClick={() => setBulkUploadShown(true)}
                    disabled={!hasPermissions('update-companies', permissions)}
                    disabledReason="No tienes permisos"
                />
            </article>
            <div className={'view-filtering-section'}>
                <span className={'filtering-label'}>Buscar por:</span>
                <FilteringButton
                    text={'ID'}
                    defaultValue={params.get('id')}
                    onSetFilter={(id) => addFilter('id', id)}
                />
                <FilteringButton
                    text={'Nombre'}
                    defaultValue={params.get('name')}
                    onSetFilter={(amount) => addFilter('name', amount)}
                />
            </div>
            <Table
                header={[
                    'ID',
                    'Nombre',
                    'Persona contacto',
                    'Dirección',
                    'Creada el',
                ]}
                data={tableRows}
                currentPage={Number(params.get('page'))}
                totalPages={totalPages}
                onPageChange={goToPage}
                onItemSelected={goToCompanyDetails}
            />
            {createCompanyShown && (
                <CreateCompany
                    onDiscard={() => setCreateCompanyShown(false)}
                    onConfirm={createCompany}
                />
            )}
            {bulkUploadShown && (
                <BulkUploadModal
                    companies={searchedCompanies}
                    plans={plans}
                    onSearchCompany={searchCompanyByName}
                    onDiscard={() => setBulkUploadShown(false)}
                    onSubmit={submitBulkUpload}
                />
            )}
            <Toast state={toast.state} text={toast.text} shown={toast.shown} />
        </View>
    );
};

const BulkUploadModal = ({
    plans,
    companies,
    onSearchCompany,
    onDiscard,
    onSubmit,
}: {
    plans: Plan[];
    companies: Company[];
    onSearchCompany: (query: string) => void;
    onDiscard: () => void;
    onSubmit: (data: SubmitBulkUpload) => void;
}) => {
    const [company, setCompany] = useState<number>();
    const [action, setAction] = useState(0);
    const [plan, setPlan] = useState<number>();
    const [file, setFile] = useState<File>();
    const [errorMessage, setErrorMessage] = useState('');

    const mapPlansToOptions = (plans: Plan[]): SelectOption[] => {
        return plans.map((plan) => ({
            value: plan.id,
            displayText: plan.description,
        }));
    };

    const submitData = () => {
        if (!file) {
            return setErrorMessage('Debes seleccionar un archivo');
        }
        if (!action) {
            return setErrorMessage('Debes seleccionar una acción');
        }
        if (action === 1 && !plan) {
            return setErrorMessage('Debes seleccionar un plan');
        }
        if (!company) {
            return setErrorMessage('Debes seleccionar una compañía');
        }
        setErrorMessage('');
        onSubmit({
            planId: plan,
            action: action,
            file: file,
            company: company,
        });
    };

    return (
        <Modal onConfirm={submitData} onDiscard={onDiscard}>
            <div className={styles.bulkUploadModal}>
                <h4>Carga masiva de usuarios corporativos</h4>
                <span>
                    Escoge el archivo que contenga los usuarios que quieres
                    activar/desactivar
                </span>
                <Note>
                    <span style={{ fontSize: 12 }}>
                        Debe ser un archivo.csv separado por punto y coma (;)
                        donde la primera columna es el tipo de documento (V, E o
                        P) y la segunda columna es el número del documento. El
                        archivo no debe llevar encabezado.
                    </span>
                </Note>
                <FileInput
                    accept={['text/csv']}
                    placeholder={'Haz click para escoger un archivo'}
                    onFile={setFile}
                />
                <label>Qué acción deseas realizar</label>
                <CustomSelect
                    options={[
                        { value: 1, displayText: 'Activar' },
                        { value: 2, displayText: 'Desactivar' },
                    ]}
                    placeholder={'Escoge la acción'}
                    width={'100%'}
                    onSelect={setAction}
                />
                {action === 1 && (
                    <>
                        <label>Escoge un plan</label>
                        <CustomSelect
                            options={mapPlansToOptions(plans)}
                            placeholder={'Escoge el plan'}
                            width={'100%'}
                            onSelect={setPlan}
                        />
                    </>
                )}
                <label>Selecciona la compañía</label>
                <CompanySearchInput
                    placeholder={'Escribe el nombre de la compañía'}
                    companies={companies}
                    onSearch={onSearchCompany}
                    onSelect={setCompany}
                />
                {errorMessage && (
                    <span className={styles.errorMessage}>{errorMessage}</span>
                )}
            </div>
        </Modal>
    );
};

export default CompaniesView;
